import { useState, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { ReactNode } from "react";
import toast from "react-hot-toast";
import { Images } from "../../utils/imageConstants";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { getAuthToken, setPremiumAccessInStorage } from "../../helper/helper";
import { AppDialog } from "../../components/common/CustomMdal";
import type { SubscriptionPlansResponse, SubscriptionPlan, CheckoutStateResponse } from "./types/subscription";
import CheckoutModal from "./components/CheckoutModal";
import cancelCrossIcon from "../../assets/images/cross_icon.png";
import successModalIcon from "../../assets/images/icons/success_modal.png";
import "../../styles/subscription/pricingPlans.scss";
import { ImFontSize } from "react-icons/im";

interface PricingFeature {
  text: string;
}

interface PricingPlan {
  id: string;
  name: string;
  price: string;
  priceInINR: string;
  duration: string;
  period: number;
  periodUnit: string;
  features: PricingFeature[];
  isPopular?: boolean;
  packageId?: string;
}

const EXCHANGE_RATES_CACHE_KEY = "pricing_exchange_rates";
const CACHE_VALID_MS = 24 * 60 * 60 * 1000; // 24 hours

type PaymentPopupStatus = "verifying" | "success" | "failed" | "pending" | "cancelled";

const PricingPlans = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [planType, setPlanType] = useState<"streaming" | "elite">("streaming");
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [paymentPopupOpen, setPaymentPopupOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentPopupStatus>("verifying");
  const [paymentReferenceId, setPaymentReferenceId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("₹ --");

  const [currency, setCurrency] = useState<string>("INR");
  const [, setExchangeRate] = useState<number>(1); // Kept for geo/rate logic; display uses backend price as-is
  const [isCurrencyReady, setIsCurrencyReady] = useState(false);

  // Fetch subscription plans in user's currency - plan IDs determine Chargebee checkout display
  //   const {
  //     data: plansResponse,
  //     isLoading,
  //     error,
  //   } = useQuery<SubscriptionPlansResponse>({
  //     queryKey: [QUERY_KEY.getSubscriptionPlans, currency],
  //     queryFn: async () => {
  //       const response = await Api.get<SubscriptionPlansResponse>(QUERY_KEY.getSubscriptionPlans, {
  //         params: { currency },
  //       });
  //       return response.data;
  //     },
  //     enabled: isCurrencyReady,
  //     staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  //   });

  // Currency detection and exchange rate fetching
  useEffect(() => {
    let mounted = true;

    const finishWithINR = () => {
      if (mounted) {
        setCurrency("INR");
        setExchangeRate(1);
        setIsCurrencyReady(true);
      }
    };

    const applyRates = (detectedCurrency: string, rate: number) => {
      if (mounted) {
        setCurrency(detectedCurrency);
        setExchangeRate(rate);
        setIsCurrencyReady(true);
      }
    };

    (async () => {
      try {
        const geoRes = await fetch("https://ipapi.co/json/");
        const geo = await geoRes.json();
        // Normalize to uppercase; handle error responses (e.g. rate limit, reserved IP)
        const rawCurrency = geo?.error === true ? null : (geo?.currency ?? null);
        const detectedCurrency = (typeof rawCurrency === "string" ? rawCurrency.toUpperCase() : null) || "INR";

        if (detectedCurrency === "INR") {
          finishWithINR();
          return;
        }

        // Try cached rates first
        try {
          const cached = localStorage.getItem(EXCHANGE_RATES_CACHE_KEY);
          if (cached) {
            const { timestamp, rates } = JSON.parse(cached) as {
              timestamp: number;
              rates: Record<string, number>;
            };
            if (Date.now() - timestamp < CACHE_VALID_MS && rates[detectedCurrency] != null) {
              applyRates(detectedCurrency, rates[detectedCurrency]);
              return;
            }
          }
        } catch {
          // Ignore cache parse errors
        }

        // Fetch fresh rates
        try {
          const ratesRes = await fetch("https://open.er-api.com/v6/latest/INR");
          const ratesData = await ratesRes.json();

          if (ratesData?.result === "success" && ratesData?.rates?.[detectedCurrency] != null) {
            const rate = ratesData.rates[detectedCurrency] as number;
            localStorage.setItem(
              EXCHANGE_RATES_CACHE_KEY,
              JSON.stringify({
                timestamp: Date.now(),
                rates: ratesData.rates,
              }),
            );
            applyRates(detectedCurrency, rate);
            return;
          }
        } catch {
          // Fallback to cached rates if fetch fails
          try {
            const cached = localStorage.getItem(EXCHANGE_RATES_CACHE_KEY);
            if (cached) {
              const { rates } = JSON.parse(cached) as { rates: Record<string, number> };
              if (rates[detectedCurrency] != null) {
                applyRates(detectedCurrency, rates[detectedCurrency]);
                return;
              }
            }
          } catch {
            // Ignore
          }
        }

        finishWithINR();
      } catch {
        finishWithINR();
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Backend returns price in smallest unit (cents for USD, paise for INR)
  // Divide by 100 to match Chargebee checkout display
  const formatPrice = useCallback(
    (price: number): string => {
      const amount = price / 100;
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    [currency],
  );

  // Format price in INR only (for CheckoutModal - paise to rupees)
  const formatPriceInINR = useCallback((price: number): string => {
    const rupees = price / 100;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(rupees);
  }, []);

  // Format duration display
  const formatDuration = (period: number, unit: string): string => {
    // Handle uppercase units from API (MONTH, YEAR, DAY)
    const normalizedUnit = unit.toLowerCase();
    const unitText =
      period === 1
        ? normalizedUnit.slice(0, -1) // Remove 's' from plural (e.g., "months" -> "month")
        : normalizedUnit;
    return `for ${period} ${unitText}`;
  };

  // Format feature name for display
  const formatFeatureName = (feature: { featureName: string; featureType: string; value?: string }): string => {
    // For NUMERIC features, show the value
    if (feature.featureType === "NUMERIC" && feature.value) {
      return `${feature.value} ${feature.featureName}`;
    }
    // For BOOLEAN features, just show the name
    if (feature.featureType === "BOOLEAN") {
      return feature.featureName;
    }
    // For unlimited features
    if (feature.featureType === "unlimited") {
      return `${feature.featureName} (Unlimited)`;
    }
    return feature.featureName;
  };

  // Transform API data to UI format
  //   const transformPlansToUI = useMemo(() => {
  //     if (!plansResponse?.data || plansResponse.data.length === 0) {
  //       return { streaming: [], elite: [] };
  //     }

  //     const streamingPlans: PricingPlan[] = [];
  //     const elitePlans: PricingPlan[] = [];

  //     plansResponse.data.forEach((plan: SubscriptionPlan) => {
  //       const planNameLower = plan.planName.toLowerCase();

  //       // Determine if this is a streaming or elite plan based on plan name
  //       const isStreaming = planNameLower.includes("streaming");
  //       const isElite = planNameLower.includes("elite");

  //       plan.planPackages.forEach((pkg) => {
  //         const transformedPlan: PricingPlan = {
  //           period: pkg.period,
  //           id: pkg.packageId,
  //           packageId: pkg.packageId,
  //           name: pkg.packageName,
  //           price: formatPrice(pkg.price),
  //           priceInINR: formatPriceInINR(pkg.price),
  //           duration: formatDuration(pkg.period, pkg.periodUnit.toLowerCase()),
  //           periodUnit: pkg.periodUnit,
  //           features: pkg.planFeatures.map((feature) => ({
  //             text: formatFeatureName(feature),
  //           })),
  //           // Only show popular badge if it's explicitly set by backend
  //           isPopular: pkg.isPopular === true,
  //         };

  //         if (isStreaming) {
  //           streamingPlans.push(transformedPlan);
  //         } else if (isElite) {
  //           elitePlans.push(transformedPlan);
  //         } else {
  //           // Default to streaming if type cannot be determined
  //           streamingPlans.push(transformedPlan);
  //         }
  //       });
  //     });

  //     return { streaming: streamingPlans, elite: elitePlans };
  //   }, [plansResponse, formatPrice, formatPriceInINR]);

  //   const currentPlans = transformPlansToUI[planType];
  const currentPlans: any[] = [];

  const clearCheckoutQueryParams = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("checkout");
    nextParams.delete("id");
    nextParams.delete("state");
    setSearchParams(nextParams, { replace: true });
  };

  // const getMembershipValidTill = (): string => {
  //     const date = new Date();
  //     date.setFullYear(date.getFullYear() + 1);
  //     return date.toLocaleDateString('en-IN', {
  //         day: '2-digit',
  //         month: 'long',
  //         year: 'numeric',
  //     });
  // };

  const normalizeCheckoutState = (value: string | null | undefined): string => {
    if (!value) return "";
    return value.toLowerCase().trim();
  };

  const resolvePopupStatus = (state: string): PaymentPopupStatus => {
    if (state === "succeeded" || state === "success") return "success";
    if (state === "cancelled" || state === "canceled") return "cancelled";
    if (state === "failed" || state === "failure") return "failed";
    if (state === "created" || state === "requested" || state === "acknowledged" || state === "pending") return "pending";
    return "failed";
  };

  const checkoutQueryKey = searchParams.toString();

  useEffect(() => {
    const params = new URLSearchParams(checkoutQueryKey);
    const checkoutParam = params.get("checkout");
    const checkoutId = params.get("id");
    const stateParam = params.get("state");

    if (!checkoutParam && !checkoutId && !stateParam) return;

    let mounted = true;

    if (checkoutId) {
      setPaymentReferenceId(checkoutId);
    }

    try {
      const storedMeta = window.localStorage.getItem("checkout_plan_meta");
      if (storedMeta) {
        const parsed = JSON.parse(storedMeta) as { planPrice?: string };
        if (parsed?.planPrice) {
          setPaymentAmount(parsed.planPrice);
        }
      }
    } catch {
      setPaymentAmount("₹ --");
    }

    const checkPaymentStatus = async () => {
      setPaymentPopupOpen(true);
      setPaymentStatus("verifying");

      if (normalizeCheckoutState(checkoutParam) === "cancelled") {
        if (mounted) {
          setPaymentStatus("cancelled");
        }
        return;
      }

      try {
        if (checkoutId) {
          const response = await Api.get<CheckoutStateResponse>(QUERY_KEY.getCheckoutState, {
            params: { id: checkoutId },
          });

          const apiState = normalizeCheckoutState(response.data?.data);
          const fallbackState = normalizeCheckoutState(stateParam || checkoutParam);
          const finalState = apiState || fallbackState;
          const resolvedStatus = resolvePopupStatus(finalState);

          if (mounted) {
            setPaymentStatus(resolvedStatus);
          }
          if (resolvedStatus === "success") {
            setPremiumAccessInStorage();
          }
          return;
        }

        const fallbackStatus = resolvePopupStatus(normalizeCheckoutState(stateParam || checkoutParam));
        if (mounted) {
          setPaymentStatus(fallbackStatus);
        }
        if (fallbackStatus === "success") {
          setPremiumAccessInStorage();
        }
      } catch {
        const fallbackStatus = resolvePopupStatus(normalizeCheckoutState(stateParam || checkoutParam));
        if (mounted) {
          setPaymentStatus(fallbackStatus);
        }
        if (fallbackStatus === "success") {
          setPremiumAccessInStorage();
        }
      }
    };

    checkPaymentStatus();

    return () => {
      mounted = false;
    };
  }, [checkoutQueryKey]);

  const paymentPopupContent = useMemo(() => {
    type PaymentPopupContent = {
      icon: ReactNode;
      iconClass: string;
      iconImage?: string;
      title: string;
      description: string;
      buttonText: string;
      disableAction: boolean;
    };

    switch (paymentStatus) {
      case "verifying":
        return {
          icon: "…",
          iconClass: "is-verifying",
          title: "Verifying your payment",
          description: "Please wait while we confirm your checkout status.",
          buttonText: "Checking...",
          disableAction: true,
        } as PaymentPopupContent;
      case "success":
        return {
          icon: "✓",
          iconClass: "is-success",
          title: "Payment successful",
          description: "Your subscription is active now. Enjoy premium access.",
          buttonText: "Continue",
          disableAction: false,
        } as PaymentPopupContent;
      case "pending":
        return {
          icon: <span className="payment-progress-spinner" aria-hidden />,
          iconClass: "is-pending",
          title: "Payment in progress",
          description: "We’re confirming your transaction. This may take a few seconds. Do not close the app or refresh this page.",
          buttonText: "Try Again",
          disableAction: false,
        } as PaymentPopupContent;
      case "cancelled":
        return {
          icon: "",
          iconClass: "is-cancelled",
          iconImage: cancelCrossIcon,
          title: "Payment cancelled",
          description: " We couldn’t complete your payment. Please check your card details or try another payment method.",
          buttonText: "Try Again",
          disableAction: false,
        } as PaymentPopupContent;
      case "failed":
      default:
        return {
          icon: "×",
          iconClass: "is-failed",
          title: "Payment failed",
          description: "We could not confirm your payment. Please try again.",
          buttonText: "Try again",
          disableAction: false,
        } as PaymentPopupContent;
    }
  }, [paymentStatus]);

  const handleSubscribe = (plan: PricingPlan) => {
    // Check if user is authenticated
    const token = getAuthToken();
    if (!token) {
      toast.error("Please sign in to subscribe");
      // Optionally redirect to login
      navigate("/");
      return;
    }

    if (!plan.packageId) {
      toast.error("Invalid plan selected");
      return;
    }

    // Open checkout modal
    setSelectedPlan(plan);
    setCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => {
    if (!checkoutModalOpen) return;
    setCheckoutModalOpen(false);
    setSelectedPlan(null);
  };

  const handleClosePaymentPopup = () => {
    if (paymentStatus === "verifying") return;
    setPaymentPopupOpen(false);
    clearCheckoutQueryParams();
  };

  // Loading state (currency detection or plans fetch)
  if (!isCurrencyReady) {
    return (
      <div className="pricing-plans-page">
        <div className="pricing-plans-container">
          <div className="pricing-header">
            <h1 className="pricing-title">CricCenter Pricing Plans</h1>
            <p className="pricing-subtitle" style={{ fontSize: "1.55rem" }}>
              Coming soon...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  //   if (error) {
  //     return (
  //       <div className="pricing-plans-page">
  //         <div className="pricing-plans-container">
  //           <div className="pricing-header">
  //             <h1 className="pricing-title">CricCenter Pricing Plans</h1>
  //             <p className="pricing-subtitle">Failed to load plans. Please try again later.</p>
  //           </div>
  //         </div>
  //       </div>
  //     );
  //   }

  return (
    <div className="pricing-plans-page" data-currency-ready={isCurrencyReady}>
      <div className="pricing-plans-container">
        {/* Header Section */}
        <div className="pricing-header">
          <h1 className="pricing-title">CricCenter Pricing Plans</h1>
          <p className="pricing-subtitle">Unlock the full CricCenter experience — AI tools, smart stats, and hassle-free streaming</p>
        </div>

        {/* Toggle Switch */}
        {/* <div className="plan-toggle-wrapper">
          <div className="plan-toggle">
            <button className={`toggle-option ${planType === "elite" ? "active" : ""}`} onClick={() => setPlanType("elite")}>
              Elite
            </button>
            <button className={`toggle-option ${planType === "streaming" ? "active" : ""}`} onClick={() => setPlanType("streaming")}>
              Streaming
            </button>
          </div>
        </div> */}

        {/* Pricing Cards */}
        <div className="pricing-cards-wrapper">
          {currentPlans.length > 0 ? (
            currentPlans.map((plan) => (
              <div key={plan.id} className={`pricing-card ${plan.isPopular ? "popular" : ""}`}>
                {plan.isPopular && <div className="popular-badge">POPULAR</div>}
                <div className="pricing-card-content">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">{plan.price}</div>
                  <div className="plan-duration">
                    {plan.period} {plan.periodUnit}
                  </div>
                  <div className="plan-divider"></div>
                  <ul className="plan-features">
                    {plan.features.map((feature: any, index: number) => (
                      <li key={index} className="feature-item">
                        <img src={Images.pricing_tick} alt="check" className="" />
                        <span>{feature.text}</span>
                      </li>
                    ))}
                  </ul>
                  <button className="subscribe-button" onClick={() => handleSubscribe(plan)}>
                    Subscribe
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "white", padding: "40px", fontSize: "1.5rem" }}>Coming soon...</div>
          )}
        </div>

        {/* Disclaimer */}
        {/* <div className="pricing-disclaimer">
          <p>
            By tapping Subscribe, your subscription will auto-renew for the same price and package length until you cancel via App Store settings and you agree to our{" "}
            <a href="#" className="terms-link">
              Terms & Conditions
            </a>
            .
          </p>
        </div> */}
      </div>

      {/* Checkout Modal */}
      {selectedPlan && <CheckoutModal isOpen={checkoutModalOpen} onClose={handleCloseCheckoutModal} planId={selectedPlan.packageId || ""} planName={selectedPlan.name} planPrice={selectedPlan.price} currency={currency} />}

      <AppDialog
        open={paymentPopupOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClosePaymentPopup();
          }
        }}
        size={566}
        className={`payment-status-modal is-${paymentStatus}-modal`}
        showCloseIcon={false}
      >
        {paymentStatus === "success" ? (
          <div className="payment-success-content">
            <div className="payment-success-top">
              <img src={successModalIcon} alt="success" className="payment-success-icon-image" />
              <h3 className="payment-success-title">Payment Successful !</h3>
              <p className="payment-success-subtitle">
                Your Payment has been processed successfully.
                {/* <br />
                                You will receive receipt to your email. */}
              </p>
            </div>

            <div className="payment-success-membership">
              <div>
                <h4>You are now a Premium member!</h4>
                {/* <p>Membership valid till {getMembershipValidTill()}</p> */}
              </div>
              {/* <button type="button" className="download-invoice-link">
                                Download Tax Invoice
                            </button> */}
            </div>

            <div className="payment-success-details">
              <div className="details-row">
                <span>Subscription Amount</span>
                <strong>{paymentAmount}</strong>
              </div>
              {/* <div className="details-row">
                                <span>Transaction ID</span>
                                <strong>{paymentReferenceId || '--'}</strong>
                            </div> */}
              {/* <div className="details-row">
                                <span>Payment Method</span>
                                <strong className="is-muted">via credit card</strong>
                            </div> */}
              {/* <div className="details-row">
                                <span>Membership stating date</span>
                                <strong>{new Date().toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'long',
                                    year: 'numeric',
                                })}</strong>
                            </div> */}
            </div>

            {/* <div className="payment-success-note">
                            Tax Invoice has been sent to <em>your registered email</em>
                        </div> */}

            <button className="payment-status-action" onClick={handleClosePaymentPopup}>
              Done
            </button>
          </div>
        ) : (
          <div className="payment-status-content">
            <div className={`payment-status-icon ${paymentPopupContent.iconClass} ${paymentPopupContent.iconImage ? "has-image" : ""}`}>
              {paymentPopupContent.iconImage ? <img src={paymentPopupContent.iconImage} alt="payment status" className="payment-status-icon-image" /> : paymentPopupContent.icon}
            </div>
            <h3 className="payment-status-title">{paymentPopupContent.title}</h3>
            <p className="payment-status-description">{paymentPopupContent.description}</p>
            <button className="payment-status-action" onClick={handleClosePaymentPopup} disabled={paymentPopupContent.disableAction}>
              {paymentPopupContent.buttonText}
            </button>
          </div>
        )}
      </AppDialog>
    </div>
  );
};

export default PricingPlans;
